import {
  Arrange,
  Button,
  Spacer,
  SvgArrowForward,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import _capitalize from 'lodash/capitalize';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import create from 'zustand';

import { ROLLOUT_AUTO_JOIN_AT_REGISTRATION } from 'constants/featureFlag';
import { ONBOARDING_STEP_COMPLETE } from 'constants/metrics';
import * as analytics from 'utilities/analytics';
import { incrementMetric } from 'utilities/metrics';
import * as sentry from 'utilities/sentry';
import { isValidWorkspaceName } from 'utilities/workspace';

import AutoJoinSection from './AutoJoinSection';
import createWorkspace from './createWorkspace.graphql';
import { JOIN_REQUEST_STATES, WORKSPACE_CREATE_STEP } from '../../constants';
import { CustomizedCta, MobileScrollContainer } from '../../cta';
import { PageHeader, PageSubHeader } from '../../headers';
import { markSelfServeInviteAsAccepted } from '../../utilities';
import { COMPLETED_ONBOARDING_STEP } from '../../../../constants/events';
import { PRIMARY_WEB_APP } from '../../../../constants/onboarding';
import { selectCurrentUser } from '../../../../selectors/user';
import { getGraphQLClient } from '../../../../utilities/graphql';
import { TEAM_BETA_TAG } from '../../../../../../shared/constants/tags';
import { WORKSPACE_PLAN_BUSINESS } from '../../../../../../shared/constants/workspacePlans';
import { WORKSPACE_CREATION_METHOD_SIGNUP_FLOW } from '../../../../../../shared/constants/workspaces';
import {
  isPublicDomain,
  parseDomain,
} from '../../../../../../shared/utilities/email';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import WorkspaceNameFormField from 'components/workspace-name-form-field';

export const useWorkspaceName = create(set => ({
  workspaceName: '',
  touched: false,
  setWorkspaceName: workspaceName => set({ workspaceName, touched: true }),
}));

function WorkspaceCreateStep({
  selfServeInvitePlan,
  nextClickHandler,
  backClickHandler,
  joinRequestState,
  isMobile,
}) {
  let workspaceNamePlaceholder = '';
  const currentUser = useSelector(selectCurrentUser);
  const emailDomain = parseDomain(currentUser.email);

  const domainParsed = emailDomain?.split('.');
  const isWorkDomain = !isPublicDomain(emailDomain);

  // If the user has a gmail, yahoo, etc email domain,
  // use their first name for their workspace placeholder
  if (!isWorkDomain) {
    workspaceNamePlaceholder = `${currentUser.first_name}'s Workspace`;
  } else {
    // use their email domain as the placeholder (e.g. "loom" for "loom.com")
    workspaceNamePlaceholder = _capitalize(domainParsed?.[0]);
  }

  const { workspaceName, setWorkspaceName, touched } = useWorkspaceName();
  const [defaultJoinRole, setDefaultJoinRole] = useState(null);
  const [autoJoinEnabled, setAutoJoinEnabled] = useState(true);
  const inputElement = useRef(null);

  const [loading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!touched) {
      setWorkspaceName(workspaceNamePlaceholder);
    }
  }, [setWorkspaceName, touched, workspaceName, workspaceNamePlaceholder]);

  useEffect(() => {
    if (inputElement.current) {
      inputElement.current.focus();
    }
  }, []);

  const submitHandler = e => {
    e.preventDefault();
    saveWorkspace(workspaceName);
  };

  const saveWorkspace = async workspaceName => {
    setIsLoading(true);
    const client = getGraphQLClient();

    try {
      const {
        data: { newWorkspace },
      } = await client.mutate({
        mutation: createWorkspace,
        variables: {
          name: workspaceName,
          workspaceType: selfServeInvitePlan || WORKSPACE_PLAN_BUSINESS,
          tags: [TEAM_BETA_TAG],
          creationMethod: WORKSPACE_CREATION_METHOD_SIGNUP_FLOW,
          autoJoinEnabled,
          defaultJoinRole,
        },
      });

      analytics.track(COMPLETED_ONBOARDING_STEP, {
        step: WORKSPACE_CREATE_STEP,
        organization_id: newWorkspace?.id,
        is_simiplified_onboarding: true,
        source: PRIMARY_WEB_APP,
      });

      incrementMetric(ONBOARDING_STEP_COMPLETE, {
        step: WORKSPACE_CREATE_STEP,
        source: PRIMARY_WEB_APP,
      });

      setIsLoading(false);

      await markSelfServeInviteAsAccepted(currentUser.email);

      nextClickHandler();
    } catch (err) {
      sentry.logException(err, 'Error creating new workspace');
      setIsLoading(false);
    }
  };

  let title = 'What is the name of your Workspace?';
  let subtitle = 'A Workspace is where all videos can be accessed.';

  if (joinRequestState === JOIN_REQUEST_STATES.REQUESTED) {
    title = 'We sent your request!';
    subtitle =
      'We’ll email you when your request is approved. In the meantime, ' +
      'you can create a new, separate Workspace for yourself and the people you invite.';
    // we skip invite teammates if they requested access to another workspace
  }

  return (
    <>
      <form className="height:full">
        <MobileScrollContainer isMobile={isMobile} offset="8.5rem">
          <Spacer left={1} right={1}>
            <PageHeader text={title} />
            <Spacer top="large" bottom={{ default: 'large', medium: 'xlarge' }}>
              <PageSubHeader text={subtitle} />
            </Spacer>

            <WorkspaceNameFormField workspaceName={workspaceName}>
              <TextInput
                id="name"
                ref={inputElement}
                onChange={e => setWorkspaceName(e.target.value)}
                value={workspaceName}
              />
            </WorkspaceNameFormField>

            {isWorkDomain && (
              <FeatureFlaggedComponent
                flag={ROLLOUT_AUTO_JOIN_AT_REGISTRATION}
                activationValues={[true]}
              >
                <div className="mt:medium md-mt:large">
                  <AutoJoinSection
                    domain={emailDomain}
                    onChangeAutoJoinEnabled={setAutoJoinEnabled}
                    onChangeDefaultJoinRole={setDefaultJoinRole}
                  />
                </div>
              </FeatureFlaggedComponent>
            )}

            <CustomizedCta isMobile={isMobile}>
              <Spacer top={{ default: 0, medium: 'xlarge' }}>
                <Arrange
                  height="100%"
                  justifyContent={{ default: 'stretch', medium: 'start' }}
                  alignItems={{ default: 'end', medium: 'center' }}
                  gap={{ default: 1.875, medium: 2.75 }}
                  columns={{ default: '1fr', medium: 'auto auto' }}
                >
                  <Button
                    size="large"
                    hasFullWidth={isMobile}
                    icon={<SvgArrowForward />}
                    iconPosition="right"
                    variant="primary"
                    onClick={submitHandler}
                    isDisabled={
                      loading ||
                      !workspaceName ||
                      !isValidWorkspaceName(workspaceName)
                    }
                    id="createWorkspaceButton"
                    hasLoader={loading}
                  >
                    Continue
                  </Button>

                  {/* joinRequestState would be undefined if there were no suggested workspaces */}
                  {joinRequestState && (
                    <div>
                      <TextButton onClick={backClickHandler}>
                        Join a Workspace instead
                      </TextButton>
                    </div>
                  )}
                </Arrange>
              </Spacer>
            </CustomizedCta>
          </Spacer>
        </MobileScrollContainer>
      </form>
    </>
  );
}

export default WorkspaceCreateStep;
